import { useState } from "react";
import { createPortal } from "react-dom";
import { FONT, COLOR } from "../lib/tokens";
import { supabase } from "../lib/supabase";

// Set / change the account password. One component, three callers:
//   1. Settings → ACCOUNT → "Set or change password"
//   2. Password-recovery (email link tap → PASSWORD_RECOVERY event,
//      or the 6-digit recovery code path on the sign-in screen)
//   3. Rendered inline (asSheet=false) by the onboarding password step
// Uses supabase.auth.updateUser — the caller is always authenticated
// by the time this mounts. Floor of 8 characters, matched confirm.
export default function NewPasswordSheet({
  open = true,
  onClose,
  onSaved,
  // Recovery mode tweaks the copy ("choose a new password") and hides
  // the dismiss affordance until saved, nudging completion.
  recovery = false,
  asSheet = true,
}) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (!open) return null;

  const valid = pw.length >= 8 && pw === pw2;
  const mismatch = pw2.length > 0 && pw !== pw2;

  const save = async (e) => {
    e?.preventDefault?.();
    if (!valid || busy) return;
    setBusy(true);
    setError("");
    const { error: err } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (err) {
      setError(err.message || "Couldn't save the password.");
      return;
    }
    setDone(true);
    onSaved?.();
    // Give the success line a beat before closing sheet-style callers.
    if (asSheet) setTimeout(() => onClose?.(), 1200);
  };

  const body = (
    <form
      onSubmit={save}
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div style={{ fontFamily: FONT.forma, fontSize: 16, fontWeight: 600, color: COLOR.ink }}>
        {recovery ? "Choose a new password" : "Set your password"}
      </div>
      <div style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.muted, lineHeight: 1.5 }}>
        At least 8 characters. You can always sign in with a magic code
        or Google too.
      </div>
      <input
        type="password"
        autoComplete="new-password"
        value={pw}
        onChange={(e) => { setPw(e.target.value); setError(""); }}
        placeholder="New password"
        autoFocus
        style={{
          padding: "13px 14px", borderRadius: 10,
          border: `1px solid ${COLOR.border}`, background: COLOR.soil,
          color: COLOR.ink, fontFamily: FONT.forma, fontSize: 15,
          outline: "none",
        }}
      />
      <input
        type="password"
        autoComplete="new-password"
        value={pw2}
        onChange={(e) => { setPw2(e.target.value); setError(""); }}
        placeholder="Repeat it"
        style={{
          padding: "13px 14px", borderRadius: 10,
          border: `1px solid ${mismatch ? COLOR.rose : COLOR.border}`,
          background: COLOR.soil,
          color: COLOR.ink, fontFamily: FONT.forma, fontSize: 15,
          outline: "none",
        }}
      />
      {mismatch && (
        <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.rose }}>
          Those don't match yet.
        </div>
      )}
      {error && (
        <div style={{
          padding: "10px 12px", background: COLOR.roseDeep,
          border: `1px solid ${COLOR.roseDim}`, borderRadius: 10,
          fontFamily: FONT.forma, fontSize: 13, color: COLOR.rose,
        }}>
          {error}
        </div>
      )}
      {done ? (
        <div style={{
          padding: "12px", textAlign: "center",
          fontFamily: FONT.forma, fontSize: 13, fontWeight: 600, color: COLOR.leaf,
        }}>
          Password saved.
        </div>
      ) : (
        <button
          type="submit"
          disabled={!valid || busy}
          className="mcm-focusable"
          style={{
            padding: "14px", borderRadius: 10, border: "none",
            background: valid ? COLOR.gold : COLOR.soil,
            color: valid ? COLOR.night : COLOR.sub,
            fontFamily: FONT.forma, fontSize: 13, fontWeight: 700,
            letterSpacing: "0.06em",
            cursor: valid && !busy ? "pointer" : "not-allowed",
          }}
        >
          {busy ? "SAVING…" : "SAVE PASSWORD"}
        </button>
      )}
      {asSheet && (!recovery || done) && (
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "none", border: "none", padding: "8px 4px",
            fontFamily: FONT.forma, fontSize: 12, color: COLOR.muted,
            letterSpacing: "0.06em", cursor: "pointer",
          }}
        >
          {done ? "DONE" : "NOT NOW"}
        </button>
      )}
    </form>
  );

  if (!asSheet) return body;

  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 460,
        background: "rgba(20, 14, 8, 0.55)",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "calc(env(safe-area-inset-top, 0px) + 20px) 20px calc(env(safe-area-inset-bottom, 0px) + 20px)",
      }}
      onClick={(e) => {
        // Backdrop dismiss only when not mid-recovery (recovery nudges
        // the user to actually finish setting the password).
        if (e.target === e.currentTarget && (!recovery || done)) onClose?.();
      }}
    >
      <div style={{
        width: "100%", maxWidth: 360,
        background: COLOR.night,
        border: `1px solid ${COLOR.border}`,
        borderRadius: 16, padding: 20,
        boxShadow: "0 18px 40px rgba(20,12,4,0.35)",
      }}>
        {body}
      </div>
    </div>,
    document.body,
  );
}
